import { useEffect, useRef } from 'react';

export function isM3U8(url) {
  return typeof url === 'string' && url.includes('.m3u8');
}

// Bunny Stream serves uploaded/auto-transcribed captions as standalone WebVTT
// files next to the playlist: `.../{guid}/playlist.m3u8` -> `.../{guid}/captions/{lang}.vtt`.
// Derived from the src we already have, so no per-video CMS field is needed.
export function bunnyCaptionUrl(src, lang = 'en') {
  if (!isM3U8(src)) return null;
  const base = src.replace(/\/[^/]+\.m3u8.*$/, ''); // strip trailing "/playlist.m3u8"
  if (base === src) return null; // no .m3u8 filename found
  return `${base}/captions/${lang}.vtt`;
}

// hls.js (~110KB min+gz) is a separate async chunk, so the first Cinemagraph to
// mount on a page normally pays a round-trip for it. Kept module-level so the
// chunk is requested at most once per session and so a speculative warm kicked
// off from an earlier page (see mediaPrefetch.js) is reused rather than
// re-imported when a Cinemagraph finally mounts.
//
// The chunk is content-hashed and immutably cached, so unlike the HLS playlists
// it never goes stale — which is why this one is safe to warm on idle from any
// page, while the playlists have to wait for navigation intent.
let hlsModulePromise = null;

export function preloadHls() {
  if (typeof window === 'undefined') return null;
  if (!hlsModulePromise) {
    hlsModulePromise = import('hls.js')
      .then(({ default: Hls }) => Hls)
      .catch(() => {
        // Let a later mount retry rather than caching the failure forever.
        hlsModulePromise = null;
        return null;
      });
  }
  return hlsModulePromise;
}

// Shared HLS attach/detach pipeline used by both the Cinemagraph (autoplay loop)
// and the VideoLightbox (full player). Safari plays HLS natively; everywhere else
// we lean on hls.js. The instance is destroyed whenever `src`/`enabled` change so
// buffers are freed when a player is torn down (e.g. lightbox close).
//
// `config` is read once at attach time — pass a stable (module-level) config
// object so the effect doesn't re-run every render. Rendition selection is left
// to hls.js' own ABR plus whatever caps the caller sets in `config`
// (`capLevelToPlayerSize`, `maxHeight`, …). Nothing here pins a level: an
// earlier version forced the highest rendition on every LEVEL_SWITCHED, which
// made a 412px phone download the 1440p ladder for a hero it renders at 412px.
export default function useHlsVideo({
  videoRef,
  src,
  enabled = true,
  config = {},
}) {
  const hlsRef = useRef(null);

  useEffect(() => {
    if (!enabled || !src || !videoRef.current) return;

    const video = videoRef.current;

    // Tear down any previous instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (!isM3U8(src)) return;

    // Safari native HLS
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      return;
    }

    // hls.js is only needed for non-Safari HLS playback, so it's fetched on
    // demand rather than bundled with every page that can render video.
    let cancelled = false;

    preloadHls().then((Hls) => {
      if (cancelled || !Hls || !Hls.isSupported()) return;

      const hls = new Hls(config);

      // We render captions ourselves via a native <track>. If Bunny's manifest
      // also advertises subtitles, keep hls.js from rendering a second copy.
      hls.subtitleDisplay = false;

      hls.attachMedia(video);

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        hls.loadSource(src);
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              break;
          }
        }
      });

      hlsRef.current = hls;
    });

    return () => {
      cancelled = true;
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, enabled, videoRef]);

  return { hlsRef };
}
